import { type BindQueueState, SettleDebt, type SettleRelease, type SettleResult } from "./scroll-engine";
import { SettleFallback } from "./settle-fallback";

/** The actual frame-paced settle waits. Cancellation retires a consumer,
 * never the bind queue it was observing. */
export class SettleWaits {
  private readonly pending = new Set<{ wake: () => void; cancel: () => void }>();

  constructor(private readonly host: SettleWaits.Host) {}

  get size(): number {
    return this.pending.size;
  }

  drain(): void {
    for (const waiter of [...this.pending]) waiter.wake();
  }

  cancelAll(): void {
    for (const waiter of [...this.pending]) waiter.cancel();
  }

  wait(debt: SettleDebt, signal: Option<AbortSignal>): Promise<SettleResult> {
    if (signal?.aborted) return Promise.resolve({ kind: "cancelled" });
    const at = SettleDebt.against(debt, this.host.queue());
    if (!at.pending && !at.owed) {
      this.host.settled(debt, "fast", null);
      return Promise.resolve({ kind: "settled", via: "fast" });
    }
    return new Promise<SettleResult>((resolve) => {
      let finished = false;
      let frame: Option<number> = null;
      let rearms = 0;
      let idleRearms = 0;
      const armedAt = this.host.now();
      // The contract clock: from the later of arm time and the gesture's end.
      let contractStart = armedAt;
      const finish = (result: SettleResult): void => {
        if (finished) return;
        finished = true;
        this.pending.delete(waiter);
        signal?.removeEventListener("abort", cancel);
        if (frame != null) this.host.cancelFrame(frame);
        frame = null;
        const progress = {
          rearms,
          idleRearms,
          waitedMs: Math.round(this.host.now() - armedAt),
        };
        if (result.kind === "cancelled") {
          this.host.cancelled(debt, progress);
        } else {
          this.host.settled(debt, result.via, progress);
        }
        resolve(result);
      };
      const cancel = (): void => finish({ kind: "cancelled" });
      const waiter = {
        wake: () => {
          if (finished) return;
          // A completion notification can follow a drop-only/throw-only drain
          // or precede another consumer's required generation. Recheck this
          // consumer's debt against the live queue before certifying readiness.
          const at = SettleDebt.against(debt, this.host.queue());
          if (!at.pending && !at.owed) {
            finish({ kind: "settled", via: "drain" });
          }
        },
        cancel,
      };
      const arm = (): void => {
        frame = this.host.requestFrame(() => {
          frame = null;
          if (finished) return;
          // The pending budget is measured from the gesture's end (the last
          // user input), sampled every frame: a fling keeps the waiter alive
          // while it lasts and does not buy a fresh 500 ms after it, and
          // `waitedMs` still reports the total wait.
          const input = this.host.lastUserInputAt();
          if (input != null && input > contractStart) contractStart = input;
          const decision = SettleFallback.decide({
            ...SettleDebt.against(debt, this.host.queue()),
            idleRearms,
            elapsedMs: this.host.now() - contractStart,
          });
          if (decision.kind === "rearm") {
            rearms++;
            if (decision.countsAsIdle) idleRearms++;
            arm();
          } else {
            finish(decision.via === "fallback-exhausted"
              ? { kind: "exhausted", via: decision.via }
              : { kind: "settled", via: decision.via });
          }
        });
      };
      this.pending.add(waiter);
      signal?.addEventListener("abort", cancel, { once: true });
      arm();
    });
  }
}

export namespace SettleWaits {
  export interface Progress {
    readonly rearms: number;
    readonly idleRearms: number;
    readonly waitedMs: number;
  }

  /** Scheduling and diagnostics belong to the scroll host; the policy and
   * terminal cleanup above are shared by production and lifecycle tests. */
  export interface Host {
    queue(): BindQueueState;
    /** The last user scroll event, else `null`. The pending budget is
     *  measured from the LATER of arm time and this — the gesture's end — so
     *  a fling never buys a fresh 500 ms after it. */
    lastUserInputAt(): Option<number>;
    now(): number;
    requestFrame(callback: () => void): number;
    cancelFrame(id: number): void;
    settled(debt: SettleDebt, via: SettleRelease, progress: Option<Progress>): void;
    cancelled(debt: SettleDebt, progress: Progress): void;
  }
}
